import { useState, useEffect, Fragment, useContext, type PropsWithChildren } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { SessionContext } from '../../../utility/useSession';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { IWalkaroundReportList, api } from '../../../logic/api';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface WaReportDownloaderProps {
    callBack: () => void;
    walkaroundResultIds: number[];
    show: boolean;
    reportParams: IWalkaroundReportList,
}

const WaReportDownloader = ({ walkaroundResultIds, show, children, reportParams, callBack }: PropsWithChildren<WaReportDownloaderProps>) => {
    const [downloading, setDownloading] = useState<boolean>(false);
    const [fetchingIds, setFetchingIds] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [totalFiles, setTotalFiles] = useState<number>(0);
    const session = useContext(SessionContext);
    const fetchIds = api.hooks.walkaroundReport.list();
    const { t } = useTranslation();

    const fetchAllIds = async () => {
        return new Promise<number[]>((accept, reject) => {
            fetchIds.send(session.current.context, {
                ...reportParams,
                idsOnly: true,
            }, (data) => {
                if (data == null) {
                    reject();
                    return;
                }
                accept(data.map((x) => x.id));
            });
        });

    };
    useEffect(() => {

        const downloadFiles = async () => {
            if (!show) return;
            if (downloading) return;

            setDownloading(true);


            let waResultIds: number[] = [];

            const indx = walkaroundResultIds.indexOf(-1);
            if (indx != -1) {
                setFetchingIds(true);
                const ids = await fetchAllIds();
                waResultIds = [...ids];
                //Filter out any ids listed in walkaroundResultIds
                waResultIds = waResultIds.filter((x) => walkaroundResultIds.indexOf(x) == -1);
                setFetchingIds(false);

            } else {
                waResultIds.push(...walkaroundResultIds);
            }

            setProgress(0);
            setTotalFiles(waResultIds.length);

            const zip = new JSZip();

            for (let i = 0; i < waResultIds.length; i++) {
                const url = `${session.current.context.url}/documents/generateBusWalkaroundPDFStream`;
                const id = waResultIds[i];

                try {
                    const response = await fetch(url, {
                        method: 'POST',
                        body: JSON.stringify({ walkaroundResultId: id }),
                        headers: {
                            'Content-Type': 'application/json',
                            "authorization": session.current.context.headers.authorization
                        }
                    });

                    if (!response.ok) {
                        throw new Error(t("walkarounds.failedToDownload", { file: id }));
                    }
                    const contentDisposition = response.headers.get('content-disposition');
                    const contentDispositionFilename = (contentDisposition?.split('filename=')[1] ?? "").split(";")[0] ?? `WALK${id.toString().padStart(5, "0")}.pdf`;

                    const blob = await response.blob();

                    if (waResultIds.length == 1) {
                        saveAs(blob, contentDispositionFilename);
                        setDownloading(false);
                        setProgress(0);
                        callBack();
                        return;
                    }
                    zip.file(contentDispositionFilename, blob);

                    // Update progress
                    setProgress((i + 1));
                } catch (error) {
                    console.error(error);

                }
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });


            const fileDateTimeString = (value: string) => {
                if (value == null) return "";
                const dateTime = new Date(value);
                if (dateTime == null) return "";
                return format(dateTime, "yyyy-MM-dd");
            }

            const startDateStr = fileDateTimeString(reportParams.startDate);
            const endDateStr = fileDateTimeString(reportParams.endDate);

            const fileNameIncludingDate = `Bus Defects ${session.current?.site?.tenant?.name ?? ""} ${startDateStr} - ${endDateStr}.zip`;

            saveAs(zipBlob, fileNameIncludingDate);

            setDownloading(false);
            setProgress(0);
            callBack();
        };

        downloadFiles();
    }, [show, walkaroundResultIds, callBack, session]);

    return (
        <Fragment>
            {downloading && (
                <Dialog open={show}>
                    <DialogTitle>{fetchingIds == true ? "Creating download list.." : "Downloading Reports.."}</DialogTitle>
                    <DialogContent>
                        <div style={{ textAlign: "center" }}>
                            <p>{t("walkarounds.fileProgress", { count: progress, total: totalFiles })}</p>
                            <p>{t("walkarounds.fileComplete", { amount: Math.floor((progress / totalFiles) * 100) })}</p>
                            <div>
                                <progress value={(progress / totalFiles)} max={1} />
                            </div>
                        </div>
                    </DialogContent>

                </Dialog>
            )}


            {(!downloading && walkaroundResultIds.length > 0) && children}
        </Fragment>
    );
};

export default WaReportDownloader;
